import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, Share, PlusSquare } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { pushSupported, getCurrentSubscription, subscribeToPush } from "@/lib/push";

const STORAGE_KEY = "push-banner-dismissed-at";
const DISMISS_DAYS = 14;

const TEXTS = {
  de: {
    title: "Angebote & Updates direkt aufs Smartphone",
    body: "Wir senden gelegentlich exklusive Aktionen, Starlink-News und Tipps. Jederzeit abbestellbar.",
    allow: "Erlauben",
    later: "Später",
    iosTitle: "Auf iPhone? Kurz zum Homescreen hinzufügen",
    iosBody:
      "Damit Push-Nachrichten auf dem iPhone ankommen, einmal die Website zum Homescreen hinzufügen und dann erneut öffnen:",
    iosStep1: "In Safari unten auf das Teilen-Symbol tippen",
    iosStep2: "‚Zum Home-Bildschirm' wählen",
    iosStep3: "App vom Homescreen öffnen → erneut auf ‚Erlauben' tippen",
    iosDismiss: "Verstanden",
  },
  en: {
    title: "Offers & updates straight to your phone",
    body: "We occasionally send exclusive deals, Starlink news and tips. Unsubscribe any time.",
    allow: "Allow",
    later: "Later",
    iosTitle: "On iPhone? Add to Home Screen first",
    iosBody:
      "For push notifications to reach iPhones, add the site to the Home Screen once, then reopen it:",
    iosStep1: "Tap the Share icon at the bottom of Safari",
    iosStep2: "Choose 'Add to Home Screen'",
    iosStep3: "Open the app from the Home Screen → tap Allow again",
    iosDismiss: "Got it",
  },
  es: {
    title: "Ofertas y novedades directo al móvil",
    body: "Enviamos ocasionalmente ofertas exclusivas, novedades Starlink y consejos. Cancela cuando quieras.",
    allow: "Permitir",
    later: "Más tarde",
    iosTitle: "¿En iPhone? Añade primero a la pantalla de inicio",
    iosBody:
      "Para que las notificaciones lleguen al iPhone, añade la web a la pantalla de inicio y vuelve a abrirla:",
    iosStep1: "Pulsa el icono de Compartir en Safari",
    iosStep2: "Elige 'Añadir a pantalla de inicio'",
    iosStep3: "Abre la app desde la pantalla de inicio → toca Permitir",
    iosDismiss: "Entendido",
  },
};

const isIos = () => {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true);

const PushSubscribeBanner = () => {
  const { locale } = useLanguage();
  const t = TEXTS[locale] || TEXTS.de;
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "granted" || Notification.permission === "denied") return;

    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
    const ageDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    if (dismissedAt && ageDays < DISMISS_DAYS) return;

    // iOS Safari that is NOT in standalone → show A2HS hint
    if (isIos() && !isStandalone()) {
      const timer = setTimeout(() => {
        setIosMode(true);
        setShow(true);
      }, 8000);
      return () => clearTimeout(timer);
    }

    if (!pushSupported()) return;

    let timer = null;
    let cancelled = false;
    getCurrentSubscription().then((sub) => {
      if (cancelled || sub) return;
      timer = setTimeout(() => setShow(true), 8000);
    });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setShow(false);
  };

  const allow = async () => {
    setLoading(true);
    try {
      await subscribeToPush(locale);
      toast.success("Benachrichtigungen aktiviert");
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
      setShow(false);
    } catch (err) {
      toast.error(err.message || "Fehler beim Aktivieren");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  if (iosMode) {
    return (
      <div
        data-testid="push-banner-ios"
        className="fixed bottom-24 right-6 left-6 sm:left-auto sm:right-6 z-40 sm:max-w-sm rounded-3xl bg-white border border-neutral-200 shadow-[0_20px_50px_rgba(0,0,0,0.15)] p-5 animate-in fade-in slide-in-from-bottom-4 duration-500"
      >
        <button
          type="button"
          onClick={dismiss}
          data-testid="push-banner-close"
          className="absolute top-3 right-3 w-7 h-7 rounded-full grid place-items-center text-neutral-400 hover:bg-neutral-100"
          aria-label="Schließen"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-2xl bg-sky-50 grid place-items-center text-sky-700 shrink-0">
            <Bell className="w-5 h-5" />
          </div>
          <div className="pr-6">
            <div className="font-display text-base font-medium text-neutral-950">
              {t.iosTitle}
            </div>
            <p className="mt-1 text-sm text-neutral-600 leading-relaxed">{t.iosBody}</p>
          </div>
        </div>

        <ol className="mt-4 space-y-2.5 text-sm">
          <li className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-neutral-950 text-white text-[10px] grid place-items-center font-medium shrink-0 mt-0.5">
              1
            </span>
            <span className="text-neutral-700 flex items-center gap-1.5">
              <Share className="w-3.5 h-3.5 text-sky-700" />
              {t.iosStep1}
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-neutral-950 text-white text-[10px] grid place-items-center font-medium shrink-0 mt-0.5">
              2
            </span>
            <span className="text-neutral-700 flex items-center gap-1.5">
              <PlusSquare className="w-3.5 h-3.5 text-sky-700" />
              {t.iosStep2}
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-neutral-950 text-white text-[10px] grid place-items-center font-medium shrink-0 mt-0.5">
              3
            </span>
            <span className="text-neutral-700">{t.iosStep3}</span>
          </li>
        </ol>

        <div className="mt-5 flex items-center justify-end">
          <button
            onClick={dismiss}
            data-testid="push-banner-ios-dismiss"
            className="text-sm px-5 py-2 rounded-full bg-neutral-950 text-white hover:bg-black"
          >
            {t.iosDismiss}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="push-banner"
      className="fixed bottom-24 right-6 z-40 max-w-sm rounded-3xl bg-white border border-neutral-200 shadow-[0_20px_50px_rgba(0,0,0,0.15)] p-5 animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="push-banner-close"
        className="absolute top-3 right-3 w-7 h-7 rounded-full grid place-items-center text-neutral-400 hover:bg-neutral-100"
        aria-label="Schließen"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-2xl bg-sky-50 grid place-items-center text-sky-700">
          <Bell className="w-5 h-5" />
        </div>
        <div className="pr-6">
          <div className="font-display text-base font-medium text-neutral-950">{t.title}</div>
          <p className="mt-1 text-sm text-neutral-600 leading-relaxed">{t.body}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          onClick={dismiss}
          data-testid="push-banner-later"
          className="text-sm px-4 py-2 rounded-full text-neutral-600 hover:bg-neutral-100"
        >
          {t.later}
        </button>
        <button
          onClick={allow}
          disabled={loading}
          data-testid="push-banner-allow"
          className="text-sm px-5 py-2 rounded-full bg-neutral-950 text-white hover:bg-black disabled:opacity-60"
        >
          {loading ? "…" : t.allow}
        </button>
      </div>
    </div>
  );
};

export default PushSubscribeBanner;
